import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { showToast } from '@klpkonsern/ui';

import { ActionLink, IconName, Illustration } from '@klp/design';
import { ListHeader } from '@klp/design/ListHeader/ListHeader';
import {
    FilterCard,
    FilterDatePicker,
    FilterMelding,
    FilterRow,
    FilterSok,
    FilterStack,
    FilterToggle,
} from '@klp/design/FilterPanel/FilterPanel';
import {
    getAktiveStatuser,
    getListParams,
    harAktivtFilter,
} from '@klp/design/FilterPanel/listParams';
import { RegionSelector, PortfolioSelector } from '@klp/portfolio/components';
import { usePortfolio, useRegion } from '@klp/portfolio/store';
import type { PortfolioRoleFilter } from '@klp/portfolio/utils';
import { Main } from '@klp/layout';
import { contextSelectors } from '@klp/contextSelector';
import { BrukerRoller } from '@klp/user/types';
import { formatDateAPI as formatDate } from '@klp/utils/dateTimeFormatter';
import { getRoleHolderBrukerId } from '@klp/prosjekt/utils';
import { useCurrentUser } from '@klp/auth';
import { useListFilters } from '@forvalter/frontpage/hooks/useListFilters';

import { prosjektSelectors } from '../store/prosjektSlice';
import { ProsjektTable } from '../components/ProsjektTable';
import { CreateProsjekt } from '../components/CreateProsject/CreateProsjekt';
import { StatusSelector } from '@klp/design/StatusSelector/StatusSelector';
import {
    useGetProsjektProcessSimpleQuery,
    useGetProsjektStatisticsQuery,
    useGetProsjektStatusQuery,
} from '../services/rtkApi';
import { ProsjektRoutes } from '../config';
import ProsjektskjematypeSelector from '../components/ProsjektskjematypeSelector';
import { TildelAnsvarligModal } from '../components/TildelAnsvarlig/TildelAnsvarligModal';

// The roles a portfolio needs to be in the portfolio selector
const PORTEFOLJE_ROLLER: PortfolioRoleFilter = [
    'isForvalter',
    'isOkonomi',
    'isQAOkonomi',
    'isAdministrerendedirektor',
    'isForvaltningsdirektor',
    'isProsjektdirektor',
    'isUtviklingsdirektor',
    'isProsjekteier',
    'isProsjektkontroller',
    'isProsjektleder',
    'isOkonomisjef',
    'isTeknisksjef',
    BrukerRoller.Regiondirektor,
    BrukerRoller.Eiendomssjef,
];

export default function ProsjektListView() {
    const { t } = useTranslation();
    const { currentUser } = useCurrentUser();
    const { portfolio, resetPortfolioField } = usePortfolio();
    const { region, resetRegionField } = useRegion();
    const { selectedContext } = useSelector(contextSelectors);
    const { activeModalProsessInstansId } = useSelector(prosjektSelectors);

    const {
        filters,
        updateFilter,
        resetFilters,
        fraDato,
        setFraDato,
        setOppslag,
        tildeltMeg,
        setTildeltMeg,
        sokPaagaar,
        filtreLaast,
        setSokTekst,
    } = useListFilters('prosjekt');

    // How many are assigned to the user, the same count as on the front page
    const { data: statistikk } = useGetProsjektStatisticsQuery(
        { ansvarlig: currentUser?.epost },
        { skip: !currentUser?.epost },
    );
    const tildeltTil = tildeltMeg ? currentUser?.epost : undefined;

    const { data: prosjektStatusList, isLoading: prosjektStatusListIsLoading } =
        useGetProsjektStatusQuery();

    // «Tildelt meg» is sent alone. Otherwise «Alle» and empty fields are left
    // out of the request, and a search is sent without the filters.
    const apiParams = tildeltTil
        ? { tildeltTil }
        : getListParams(
              {
                  portfolio: portfolio?.brukerId,
                  region: region?.name,
                  statuses: getAktiveStatuser(
                      filters.statuses,
                      prosjektStatusList,
                  ),
                  fromDate: fraDato ? formatDate(fraDato) : undefined,
                  byggnummer: selectedContext?.id,
              },
              { sok: filters.oppslag },
          );
    // The endpoint has no paging, so the list is only fetched when a filter or
    // a search narrows it
    const kanHente = harAktivtFilter(apiParams);

    const {
        data: prosjektData,
        isLoading: prosjektIsLoading,
        isFetching: prosjektIsFetching,
        isError: prosjektIsError,
    } = useGetProsjektProcessSimpleQuery(apiParams, {
        skip: prosjektStatusListIsLoading || !kanHente,
    });

    // RTK Query keeps the last result while the query is skipped, so hide it
    // when nothing narrows the list any more
    const prosjekter = React.useMemo(
        () => (kanHente ? (prosjektData ?? []) : []),
        [prosjektData, kanHente],
    );

    // Filter projects by selected prosjektskjemaTypes
    const filteredProsjekter = React.useMemo(() => {
        if (
            tildeltMeg ||
            sokPaagaar ||
            !filters.prosjektskjemaTypes ||
            filters.prosjektskjemaTypes.length === 0
        ) {
            return prosjekter;
        }
        return prosjekter.filter((prosjekt) =>
            filters.prosjektskjemaTypes.includes(
                prosjekt.prosjektskjemaType?.typeNavn ?? '',
            ),
        );
    }, [prosjekter, filters.prosjektskjemaTypes, sokPaagaar, tildeltMeg]);

    const matchingProsjekt = prosjekter?.find(
        (prosjekt) => prosjekt.prosessInstansId === activeModalProsessInstansId,
    );

    const handleProsjektskjematypeChange = (types: Array<string>) => {
        updateFilter('prosjektskjemaTypes', types);
    };

    const handleStatusChange = (statuses: Array<string>) => {
        updateFilter('statuses', statuses);
    };

    const handleNullstillFilters = () => {
        resetPortfolioField();
        resetRegionField();
        resetFilters();
    };

    if (prosjektIsError) {
        showToast({
            body: t('general.error.message'),
            type: 'error',
        });
    }

    return (
        <div>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="prosjektchema"
                    label="breadcrumbs.project.schema"
                    href={ProsjektRoutes.projectSchema()}
                />
            </Main.Breadcrumbs>
            {activeModalProsessInstansId &&
                matchingProsjekt?.kandidatGruppe && (
                    <TildelAnsvarligModal
                        prosjektId={matchingProsjekt.prosjektId}
                        prosessInstansId={matchingProsjekt.prosessInstansId}
                        kandidatGruppe={matchingProsjekt.kandidatGruppe}
                        kandidatGruppeEier={matchingProsjekt.kandidatGruppeEier}
                        currentAnsvarlig={
                            getRoleHolderBrukerId(
                                matchingProsjekt,
                                matchingProsjekt.kandidatGruppeEier,
                            ) ?? null
                        }
                        activity={undefined}
                        region={undefined}
                    />
                )}
            <ListHeader title={t('project.title')}>
                <ActionLink
                    actionType="link"
                    href={ProsjektRoutes.projectSchemaDocumentationUrl()}
                    endIcon={
                        <Illustration
                            icon={IconName.CIRCLE_INFO}
                            klpPalette="secondary"
                        />
                    }
                    translation="project.header.documentation"
                />

                <ActionLink
                    actionType="link"
                    href={ProsjektRoutes.projectFullmakt()}
                    endIcon={
                        <Illustration
                            icon={IconName.PROFILE_FILLED}
                            klpPalette="secondary"
                        />
                    }
                    translation="project.header.fullmakt"
                />

                <CreateProsjekt />
            </ListHeader>

            <FilterCard
                loading={kanHente && (prosjektIsLoading || prosjektIsFetching)}
                filters={
                    <FilterStack>
                        <FilterSok
                            id="prosjekt-sok"
                            label={t('prosjekt.oppslag.sokLabel')}
                            value={filters.oppslag}
                            onChange={setOppslag}
                            onInputChange={setSokTekst}
                            disabled={tildeltMeg}
                            foran={
                                <FilterToggle
                                    label={t('filterPanel.tildeltMeg')}
                                    antall={statistikk?.TildeltMeg}
                                    aktiv={tildeltMeg}
                                    onChange={setTildeltMeg}
                                />
                            }
                            helperText={
                                tildeltMeg
                                    ? t('filterPanel.visTildelt')
                                    : filtreLaast
                                      ? t('filterPanel.sokUtenFiltre')
                                      : undefined
                            }
                        />
                        <FilterRow onReset={handleNullstillFilters}>
                            <PortfolioSelector
                                label="filterPanel.portefolje"
                                allLabel="filterPanel.alle"
                                filter={PORTEFOLJE_ROLLER}
                                disabled={filtreLaast}
                            />

                            <RegionSelector
                                label="filterPanel.region"
                                allLabel="filterPanel.alle"
                                disabled={filtreLaast}
                            />

                            <StatusSelector
                                label="filterPanel.status"
                                allLabel="filterPanel.alle"
                                statusList={prosjektStatusList}
                                statusListIsLoading={
                                    prosjektStatusListIsLoading
                                }
                                value={filters.statuses}
                                onChange={handleStatusChange}
                                translationPrefix="prosjekt.statuses"
                                displayMode="summary"
                                disabled={filtreLaast}
                            />

                            <ProsjektskjematypeSelector
                                selectedTypes={filters.prosjektskjemaTypes}
                                onChange={handleProsjektskjematypeChange}
                                disabled={filtreLaast}
                            />

                            <FilterDatePicker
                                label={t('filterPanel.endretEtter')}
                                value={fraDato}
                                onChange={setFraDato}
                                clearable={true}
                                disabled={filtreLaast}
                            />
                        </FilterRow>
                    </FilterStack>
                }
            >
                {kanHente ? (
                    <ProsjektTable prosjekter={filteredProsjekter} />
                ) : (
                    <FilterMelding>{t('filterPanel.velgFilter')}</FilterMelding>
                )}
            </FilterCard>
        </div>
    );
}
